/**
 * Users → Adjustments — every act that changed what the system said about a
 * question after the machine said it: region corrections, re-evaluations,
 * overrides, disputes, discards, clean-up scripts.
 *
 * Filters at the top (tenant comes from the sidebar), totals, one grouped table
 * whose rows narrow the filters when clicked, and the rows themselves, each
 * opening the analysis it changed.
 *
 * See docs/FeatureDesigns/AssessmentAndMarking/analysis-adjustments-target.md §8.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SearchFilterBar, TabStrip, DataTable, type FilterDef, type ColumnDef } from '@shared-ui/components';
import {
  ADJUSTMENT_ORIGINS, ADJUSTMENT_ORIGIN_LABELS, ADJUSTMENT_REASONS, ADJUSTMENT_REASON_LABELS,
  type AdjustmentOrigin, type AdjustmentReason,
} from '@padcanvas/shared';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { useSelectedTenantId, hasScopedTenant } from '../../components/TenantFilter';
import { formatWhen } from '../sticking-patterns/format';
import './Adjustments.css';

const KIND_LABELS: Record<string, string> = {
  region_correction: 'Region correction',
  reevaluate: 'Re-evaluation',
  revert: 'Revert',
  score_override: 'Score override',
  answer_override: 'Answer override',
  dispute: 'Dispute',
  dispute_lifted: 'Dispute lifted',
  evidence_settle: 'Reading settled',
  moment_reject: 'Moment rejected',
  diagnosis_review: 'Diagnosis reviewed',
  moment_approve: 'Moment approved',
  step_mark: 'Step marked',
  attempt_discard: 'Attempt discarded',
  content_cleanup: 'Clean-up script',
};

type GroupBy = 'kind' | 'origin' | 'reasonCode' | 'questionSet' | 'question' | 'student' | 'actor' | 'day';

/** Clicking a group narrows to it — for the groups that are a filter. */
const DRILL: Partial<Record<GroupBy, string>> = {
  kind: 'kind', origin: 'origin', reasonCode: 'reasonCode', questionSet: 'questionSetId', student: 'studentId', actor: 'actorId',
};

interface Snapshot { finalScore?: number; marksAwarded?: number; answer?: string; diagnosisKind?: string; withdrawn?: string }
interface Adjustment {
  id: string; kind: string; at: string; source: string;
  actor: { userId: string; role: string };
  studentId: string; questionId: string; questionNumber?: number; questionSetTitle?: string;
  fromSubmissionId?: string; toSubmissionId?: string;
  origin?: AdjustmentOrigin; reasonCode?: AdjustmentReason; internalComment?: string; modelNote?: string;
  before?: Snapshot; after?: Snapshot; outcome: string; error?: string;
  script?: { name: string; runId: string };
}
interface Group {
  key: string; label?: string; count: number; questions: number; attempts: number; students: number;
  meanFinalScoreDelta?: number; netMarks?: number; marksUp: number; marksDown: number; noChange: number; failed: number;
}
interface Summary { total: Group; groups: Group[]; names: Record<string, string>; truncated: boolean }
interface ListResponse { rows: Adjustment[]; names: Record<string, string>; truncated: boolean }

const EMPTY = { kind: '', origin: '', reasonCode: '', questionSetId: '', studentId: '', actorId: '', source: '', atFrom: '', atTo: '' };

function nextDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function snapshotText(s?: Snapshot): string {
  if (!s) return '—';
  const parts = [
    s.marksAwarded !== undefined ? `${s.marksAwarded} marks` : s.finalScore !== undefined ? `${s.finalScore}/10` : '',
    s.withdrawn ? s.withdrawn : '',
  ].filter(Boolean);
  return parts.join(' · ') || '—';
}

function groupLabel(by: GroupBy, g: Group, names: Record<string, string>): string {
  if (g.key === '(none)') return by === 'reasonCode' || by === 'origin' ? 'not given' : '—';
  switch (by) {
    case 'kind': return KIND_LABELS[g.key] ?? g.key;
    case 'origin': return ADJUSTMENT_ORIGIN_LABELS[g.key as AdjustmentOrigin] ?? g.key;
    case 'reasonCode': return ADJUSTMENT_REASON_LABELS[g.key as AdjustmentReason] ?? g.key;
    case 'student': return names[g.key] ?? g.key;
    case 'actor': return `${names[g.key] ?? g.key}${g.label ? ` (${g.label})` : ''}`;
    default: return g.label ?? g.key;
  }
}

export function Adjustments() {
  const navigate = useNavigate();
  const tenant = useSelectedTenantId();
  const [applied, setApplied] = useState<Record<string, string>>(EMPTY);
  const [groupBy, setGroupBy] = useState<GroupBy>('kind');

  const params = new URLSearchParams();
  if (hasScopedTenant(tenant)) params.set('tenantId', tenant);
  for (const [k, v] of Object.entries(applied)) {
    if (!v) continue;
    if (k === 'atFrom') params.set('from', new Date(`${v}T00:00:00Z`).toISOString());
    else if (k === 'atTo') params.set('to', nextDay(v));
    else params.set(k, v);
  }

  const summary = useQuery({
    queryKey: ['adjustments-summary', params.toString(), groupBy],
    queryFn: () => fetchWithAdmin<Summary>(`/console/adjustments/summary?${params}&groupBy=${groupBy}`),
  });
  const list = useQuery({
    queryKey: ['adjustments', params.toString()],
    queryFn: () => fetchWithAdmin<ListResponse>(`/console/adjustments?${params}&limit=200`),
  });

  const filterDefs: FilterDef[] = [
    { key: 'kind', kind: 'multiselect', placeholder: 'All kinds', options: Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label })) },
    { key: 'origin', kind: 'multiselect', placeholder: 'All sides', options: ADJUSTMENT_ORIGINS.map(o => ({ value: o, label: ADJUSTMENT_ORIGIN_LABELS[o] })) },
    { key: 'reasonCode', kind: 'multiselect', placeholder: 'All reasons', options: ADJUSTMENT_REASONS.map(r => ({ value: r, label: ADJUSTMENT_REASON_LABELS[r] })) },
    { key: 'source', kind: 'select', placeholder: 'App and scripts', options: [{ value: 'app', label: 'app' }, { value: 'script', label: 'script' }, { value: 'backfill', label: 'backfill' }] },
    { key: 'questionSetId', kind: 'text', placeholder: 'Assignment id' },
    { key: 'studentId', kind: 'text', placeholder: 'Student id' },
    { key: 'at', kind: 'daterange', label: 'When', fromPlaceholder: 'from', toPlaceholder: 'to' },
  ];

  const names = { ...(list.data?.names ?? {}), ...(summary.data?.names ?? {}) };
  const total = summary.data?.total;

  const groupColumns: ColumnDef<Group>[] = [
    { key: 'key', header: groupBy, mobile: 'title', render: g => <span className="adj-name">{groupLabel(groupBy, g, names)}</span> },
    { key: 'count', header: 'adjustments', render: g => g.count },
    { key: 'questions', header: 'questions', render: g => g.questions },
    { key: 'students', header: 'students', render: g => g.students },
    { key: 'netMarks', header: 'net marks', render: g => (g.netMarks === undefined ? '—' : g.netMarks > 0 ? `+${g.netMarks}` : g.netMarks) },
    { key: 'moved', header: 'up / down', className: 'adj-dim', render: g => `${g.marksUp} / ${g.marksDown}` },
    { key: 'noChange', header: 'changed nothing', className: 'adj-dim', render: g => g.noChange },
    { key: 'failed', header: 'failed', className: 'adj-dim', render: g => g.failed || '' },
  ];

  const rowColumns: ColumnDef<Adjustment>[] = [
    { key: 'at', header: 'when', mobile: 'title', className: 'adj-col-when', render: r => formatWhen(r.at) },
    { key: 'student', header: 'student', render: r => names[r.studentId] ?? r.studentId },
    {
      key: 'question', header: 'question',
      render: r => (r.questionNumber !== undefined ? `${r.questionSetTitle ?? ''} Q${r.questionNumber}` : r.questionId),
    },
    { key: 'kind', header: 'kind', render: r => KIND_LABELS[r.kind] ?? r.kind },
    {
      key: 'why', header: 'why',
      render: r => (r.origin
        ? <span>{ADJUSTMENT_ORIGIN_LABELS[r.origin]}{r.reasonCode && <> · {ADJUSTMENT_REASON_LABELS[r.reasonCode]}</>}</span>
        : <span className="adj-dim">not given</span>),
    },
    {
      key: 'change', header: 'before → after',
      render: r => (
        <span className={r.outcome === 'failed' ? 'adj-error' : r.outcome === 'no_change' ? 'adj-dim' : ''}>
          {r.outcome === 'failed' ? `failed: ${r.error ?? ''}` : `${snapshotText(r.before)} → ${snapshotText(r.after)}`}
        </span>
      ),
    },
    {
      key: 'comment', header: 'note',
      render: r => <span className="adj-note">{[r.internalComment, r.modelNote && `AI: ${r.modelNote}`].filter(Boolean).join(' — ') || ''}</span>,
    },
    {
      key: 'actor', header: 'by', className: 'adj-dim',
      render: r => (r.script ? `${r.script.name}` : `${names[r.actor.userId] ?? r.actor.userId} (${r.actor.role})`),
    },
  ];

  const drill = (g: Group) => {
    const filter = DRILL[groupBy];
    if (!filter || g.key === '(none)') return;
    setApplied(a => ({ ...a, [filter]: g.key }));
  };

  return (
    <div className="adj-page">
      <h1>Adjustments</h1>
      <p className="adj-dim adj-intro">
        Every correction, re-evaluation, override, dispute and clean-up made to a reading after the machine made it.
        The sidebar's tenant scopes the page.
      </p>

      <SearchFilterBar filters={filterDefs} values={applied} onChange={setApplied} hideSearch />

      {summary.isError && <p className="adj-error">Failed: {(summary.error as Error).message}</p>}
      {total && (
        <div className="adj-tiles">
          <Tile label="adjustments" value={total.count} />
          <Tile label="questions" value={total.questions} />
          <Tile label="attempts" value={total.attempts} />
          <Tile label="students" value={total.students} />
          <Tile label="net marks" value={total.netMarks === undefined ? '—' : total.netMarks > 0 ? `+${total.netMarks}` : total.netMarks} />
          <Tile label="changed nothing" value={total.noChange} />
        </div>
      )}
      {summary.data?.truncated && <p className="adj-warn">More rows match than the summary reads — narrow the dates.</p>}

      <TabStrip
        className="adj-tabs"
        ariaLabel="Group adjustments by"
        active={groupBy}
        onChange={setGroupBy}
        tabs={[
          { key: 'kind', label: 'Kind' },
          { key: 'origin', label: 'Side' },
          { key: 'reasonCode', label: 'Reason' },
          { key: 'questionSet', label: 'Assignment' },
          { key: 'question', label: 'Question' },
          { key: 'student', label: 'Student' },
          { key: 'actor', label: 'Who' },
          { key: 'day', label: 'Day' },
        ]}
      />
      {summary.data && (
        <DataTable
          data={summary.data.groups}
          columns={groupColumns}
          rowKey={g => g.key}
          onRowClick={DRILL[groupBy] ? drill : undefined}
          emptyMessage="No adjustments match."
        />
      )}

      <h2 className="adj-h2">Adjustments</h2>
      {list.isLoading && <p className="adj-dim">Loading…</p>}
      {list.isError && <p className="adj-error">Failed: {(list.error as Error).message}</p>}
      {list.data && (
        <>
          <p className="adj-dim">
            {list.data.rows.length}{list.data.truncated ? '+ (newest shown)' : ''} rows
          </p>
          <DataTable
            data={list.data.rows}
            columns={rowColumns}
            rowKey={r => r.id}
            onRowClick={r => {
              const id = r.toSubmissionId ?? r.fromSubmissionId;
              if (id) navigate(`/submissions/${id}`);
            }}
            emptyMessage="No adjustments match."
          />
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="adj-tile">
      <div className="adj-tile-value">{value}</div>
      <div className="adj-tile-label">{label}</div>
    </div>
  );
}
